import React, { useRef } from 'react';
import InlinePoNumberEditor from './InlinePoNumberEditor';
import InlineExpectedDeliveryEditor from './InlineExpectedDeliveryEditor';
import ExpectedDeliveryBadge from './ExpectedDeliveryBadge';
import OrderForm from './OrderForm';
import OrderInlineEditor from './OrderInlineEditor';
import OrderExpandedDetails from './OrderExpandedDetails';
import OrderRejectionLine from './OrderRejectionLine';
import { RECEIVING_LOCKED_TOOLTIP } from '../constants/statuses';
import { lineStepMap } from './OrderItemReceivedControls';

// The statuses on which a MANAGER may EDIT the expected delivery date —
// owner ruling 2026-08-19: "Editable from ordered onwards" (`ordered`,
// `partially_delivered`); `delivered` is deliberately EXCLUDED — read-only,
// historical, via `ExpectedDeliveryBadge` instead of this editor. Mirrors
// the greyed-until-ordered rule already ruled for the receive ticks
// (`RECEIVE_ENABLED_STATUSES`, `constants/statuses.js`), minus `delivered`.
const EXPECTED_DELIVERY_EDITABLE_STATUSES = ['ordered', 'partially_delivered'];

/**
 * `OrderExpandedRow` — THE shared expanded-row body for the order list pages'
 * TABLE layout, extracted from `DepartmentOrders.js` on `feat/order-expanded-
 * row-extract` (Phase 1 of the My Orders / Dept Orders convergence plan).
 *
 * 🔑 **IT RESOLVES NOTHING — same contract as `OrderActionsMenu` /
 * `OrderTableView`.** Every permission gate that used to live inline in
 * `DepartmentOrders.js` is now a prop this component reads and branches on
 * (`canManage`, `showInlineEditor`, `canUploadDelivery`, …); it computes NONE
 * of them. The PAGE resolves `useOrderAuthority().canManageOrder(detail)` and
 * `detail.can_edit` and hands the answers down. A caller that wants a
 * requester to see less passes different booleans and a different
 * `quickActionsRow` / `quickActionsHeaderGroup` node — this component does not
 * need to know why.
 *
 * 🔀 **A NEW CALLER NEEDS THE HOOK, NOT THIS COMPONENT (2026-09-27).** Since the
 * order pages' expanded rows moved into `hooks/useDeptOrderExpansion.js` and
 * `hooks/useMyOrderExpansion.js`, every prop below is built there — gates,
 * handlers, save-then-act, dialogs. A surface that wants "the same expansion"
 * (the hires Table does: `components/hires/HireOrderExpansion.js`) runs one of
 * those hooks and renders its `renderExpanded(order)`; rendering this component
 * directly would mean rebuilding every prop, i.e. a copy.
 *
 * 🚨 **THREE THINGS ARE DELIBERATELY *NOT* OWNED HERE, AND ALL THREE ARE
 * PAGE-STATE-COUPLED, NOT PERMISSION-COUPLED** — moving them would not have
 * changed who could do what, only where the state lives, so Phase 1 leaves
 * them on the page:
 *   - `adminNotesNode` — the admin-notes editor/display. Its edit buffer is a
 *     page-level `{ orderId: text }` map (`editingNotes`/`savingNotes`); the
 *     page builds the finished node exactly as it always did and hands it in.
 *   - `quickActionsRow` / `quickActionsHeaderGroup` — the Approve/Reject
 *     buttons + `OrderActionsMenu` (View/Edit/status lifecycle/Export/PDF).
 *     These are the "gated action array" the convergence plan describes: the
 *     PAGE builds them (it owns `runRowAction`, `actionBusy`, the completion
 *     dialog and the status-override footer), this component only places the
 *     finished node in the right slot for each branch.
 *
 *     🔀 **THE READ-ONLY BRANCH'S "Order Details" HEADER IS NOW `OrderForm`'s
 *     OWN, NOT A SEPARATE `OrderSectionHeader` CALL HERE (2026-08-16) —
 *     SUPERSEDES THE 2026-08-16-EARLIER NOTE THAT SAID THIS COMPONENT
 *     RENDERED ITS OWN.** See the big docblock at `readOnlyExpandedContent`
 *     below for the full design; in short, `quickActionsRow` is now handed
 *     to `OrderForm` as its `headerActions` prop — the SAME slot the edit
 *     branch already fed via `OrderInlineEditor` — rather than sitting beside
 *     a second, bespoke header this component used to build. One header
 *     component, one slot, whichever branch renders.
 *
 *     🔀 **`headerActionsBelow` JOINED IT (2026-08-17, To Approve's read-only
 *     migration onto this component)** — forwarded verbatim to `OrderForm`'s
 *     OWN `headerActionsBelow` prop, the slot built for exactly one thing:
 *     `Approvals.js`'s two-step reject reason, which must render DIRECTLY
 *     BENEATH the header bar (a textarea cannot live IN the bar itself). The
 *     PAGE still builds the finished textarea node; this component still
 *     resolves nothing about when it shows. ADDITIVE, defaults `null` — Dept
 *     Orders and My Orders pass neither `quickActionsRow`'s reject flow nor
 *     this prop, so they are BYTE-IDENTICAL before and after.
 *   - The `OrderInlineEditor` pass-through bag (`editor`) — reference data
 *     (`productions`/`suppliers`/…) and the save-then-act wiring
 *     (`saveRequestRef`/`collapseRequestSignal`/`onCollapsePromptResolved`) are page
 *     state; this component forwards them untouched, exactly as
 *     `OrderInlineEditor` itself forwards `headerActions` and `received`.
 *
 * 🔀 **THE READ-ONLY BRANCH IS NOW `components/OrderForm.js` IN `readOnly`
 * MODE — THE SAME FORM THE EDIT BRANCH RENDERS, NOT A BESPOKE PANEL
 * (2026-08-16).** Until this change the read-only branch was its own
 * hand-rolled metadata grid + line-items table (`readOnlyPanelBody`, deleted
 * with this change), which is exactly why My Orders' expanded view never
 * looked like Department Orders' — two components, two field sets, two
 * layouts, guaranteed to drift. See the docblock at `readOnlyExpandedContent`
 * below for the coexistence design (PO/cost-code/payment-type/notify — the
 * manager controls that write through dedicated endpoints and must survive
 * the swap without double-rendering).
 *
 * Only three things are STILL owned here, parameterised by props: the small
 * extras grid above the form (PO editor for a manager / expected delivery
 * date editor-or-badge / rejection reason — none of which `OrderForm` has a
 * field for), the inline-editor-vs-read-only branch itself, and
 * `OrderExpandedDetails`.
 *
 * 🔻 **ADMIN NOTES LEFT THAT GRID ON 2026-09-13 AND IS NOW HANDED TO
 * `OrderForm`'S `orderDetailsRow2Rest` SLOT ON BOTH BRANCHES** — owner: *"lets
 * move it to the order details section"*. The PAGE still builds the node and
 * this component still resolves nothing about it; only its destination moved.
 * 🔻 **AND THE SAME DAY, THE SAME RULING TOOK EXPECTED DELIVERY AND THE
 * READ-ONLY PO EDITOR INTO THE FORM TOO** — owner: *"Expected delivery can
 * also go in the Order Details section? this then clears the space occupied
 * by the admin notes and expected delivery date."* They go down as
 * `OrderForm`'s `orderDetailsRow2Lead` and `poNumberFieldSlot`, two more
 * finished nodes. **The metadata band above the form therefore now holds the
 * rejection reason ALONE, and renders nothing at all on a non-rejected
 * order** — an expanded row's first child is the Order Details panel.
 * 🔻 **SECOND-PASS RULING, SAME DAY:** Expected Delivery and Internal notes
 * now form a SECOND ROW of `OrderForm`'s Order Details grid, under PO Number
 * — owner: *"put 'expected delivery date' on a new row under PO Number.
 * Internal notes goes next to Expected delivery date"*. `orderDetailsFooter`
 * / `orderDetailsExtraField` were renamed to `orderDetailsRow2Rest` /
 * `orderDetailsRow2Lead` because both old names described the superseded
 * geometry (a full-width footer below the grid; a half cell at the end of
 * row 1). The Expected-Delivery/Admin-Notes pairing and its 14rem sub-grid
 * this component used to render are documented where they were built, beside
 * `expectedDeliveryNode` below, including the three pieces of layout
 * machinery deleted with them.
 *
 * 🪤 **`testIdPrefix` reproduces the EXACT `dept-…` testids Dept Orders always
 * had** (`DepartmentOrders.js` passes `testIdPrefix="dept"`), so every
 * existing `DepartmentOrders.expandEdit.test.js` / `actionsWithEdits` /
 * itemPreview locator resolves unchanged. A future caller (Phase 2's My
 * Orders) passes its own prefix and gets its own distinct ids — the
 * cross-cutting rule that testids must never collide across surfaces.
 * ⚠️ **THIS NO LONGER COVERS THE LINE-ITEM CELLS OR THE PO/COST-CODE/
 * PAYMENT-TYPE CONTROLS INSIDE THE READ-ONLY FORM** — those now carry
 * `OrderForm`'s OWN generic, non-prefixed testids (`po-number`,
 * `item-cost-code-${index}`, …), exactly as the edit branch's `OrderForm`
 * always has. This is safe for the SAME reason the edit branch has always
 * been safe — see the single-expand note below — and it is a genuine
 * CONTRACT CHANGE for any read-only-branch test that used to assert
 * `${testIdPrefix}-cost-code-${orderId}-${itemId}` or similar.
 *
 * 🔑 Single-expand + the `OrderForm` id-collision constraint (~54 id sites)
 * is why only ONE `OrderExpandedRow` is ever mounted with `showInlineEditor`
 * true at a time — that invariant is the CALLER's (the page's accordion
 * state), not this component's. **Since 2026-08-16 the SAME constraint
 * covers the READ-ONLY branch too**, because it now also mounts a full
 * `OrderForm`: `MyOrders.js` and `DepartmentOrders.js`'s `toggleExpand`
 * already collapse `expandedOrders` to `new Set([orderId])` on every expand
 * (never a multi-member Set), so at most one `OrderExpandedRow` — read-only
 * or editable — is ever mounted at a time on either page. Nothing in this
 * change weakens that; it relies on it more than before.
 */
function OrderExpandedRow({
  order,
  detail,
  // Whether to render the unlocked `OrderInlineEditor` form or the read-only
  // panel. Resolved by the PAGE from `detail.can_edit === true` plus whatever
  // else gates editing there (Dept Orders also requires the TABLE layout —
  // see the sanctioned-exception box in `client/src/pages/CLAUDE.md`).
  showInlineEditor,
  // `useOrderAuthority().canManageOrder(detail)`, resolved by the PAGE in the
  // order's OWN department. Gates: the PO editor, the line-item accounting
  // selects, the received-ticks column heading, the item-actions menu column,
  // and the desktop table's derived `colSpan`.
  canManage,
  // Reproduces the page's existing testid prefix (`"dept"` for Department
  // Orders) so every id below is byte-identical to what it always was.
  testIdPrefix,
  // 🔀 2026-09-28 — `(item) → [menuItem…]`, extra FINISHED items for a line's
  // own `OrderItemActionsMenu` (the hires pages' Extend / Off hire / Bring
  // back). Forwarded to `OrderForm` on BOTH branches. Default `null` — every
  // order page passes nothing and renders byte-identically.
  itemExtraActions = null,
  // 🔀 2026-09-29 — `(item) → tick | null`, the hires pages' per-line "Off
  // hired" tick for `OrderForm`'s item header (`itemHeaderTick`), forwarded on
  // BOTH branches like `itemExtraActions`. Default `null` → byte-identical.
  itemHeaderTick = null,

  formatMoney,

  // Line-item accounting selects.
  costCodes = [],
  paymentTypes = [],
  onCostCodeChange,     // (orderId, itemId, value)
  onPaymentTypeChange,  // (orderId, itemId, value)
  onItemNotifyUpdated,  // (orderId, updatedItem) — OrderItemActionsMenu

  // PO number.
  onPoNumberSaved,      // (orderId, normalisedPoNumber)

  // Expected delivery date.
  onExpectedDeliverySaved, // (orderId, normalisedDate)

  // Received ticks — the shared per-item delivery cluster
  // (`OrderItemReceivedControls` / `utils/receivedItems.js`).
  showReceivedTicks,
  canReceiveNow,
  receivingThisOrder,
  receivedCount,
  // ⚖️ THE DELIVERY DENOMINATOR, GOODS ONLY (owner ruling 2026-09-19). Passed
  // in beside `receivedCount` rather than derived here from `detail.items`, so
  // the numerator and the denominator come from ONE `countDelivery` call on the
  // page. Derived locally they would be two answers to one question, and an
  // adjustment row would land in the total while staying out of the count.
  receivableCount,
  allItemsReceived,
  onToggleItemReceived, // (orderId, itemId, current)
  onReceiveAll,         // (orderId, next)
  // 🔀 PER-LINE "ORDERED" (2026-10-28) — the step before receive. The PAGE
  // resolves `canOrderItems` (order management in the order's own dept, the
  // server's `requireOrderManagement`); `orderedCount` comes from the SAME
  // `countDelivery` call as `receivedCount`.
  canOrderItems = false,
  orderedCount = 0,
  onToggleItemOrdered,  // (orderId, itemId, current)
  onOrderAll,           // (orderId, next)

  // Pre-built, page-state-coupled nodes — see the docblock above.
  adminNotesNode = null,
  quickActionsRow = null,
  quickActionsHeaderGroup = null,
  // 🔀 THE REJECT-TEXTAREA SLOT (2026-08-17, To Approve's read-only-branch
  // migration onto this component). Forwarded VERBATIM to `OrderForm`'s own
  // `headerActionsBelow` prop — the SAME slot the edit branch already reaches
  // via `OrderInlineEditor` (`Approvals.js` passes it there today,
  // unaffected by this change). See the docblock above `readOnlyExpandedContent`
  // for why this had to be a SECOND slot rather than folded into
  // `quickActionsRow`: a textarea cannot live IN the header bar.
  // ADDITIVE and optional, defaulting to `null` — the same shape every prop
  // here uses. **Absent, `OrderForm` renders nothing extra, byte for byte**,
  // which is what keeps Dept Orders and My Orders (neither passes it)
  // untouched by this change.
  headerActionsBelow = null,

  // `OrderExpandedDetails` (Attachments / Delivery Paperwork / Activity
  // history / Approval stepper) — gates resolved by the page, this component
  // only places the finished panel.
  canUploadDelivery = false,
  canRemoveFile = () => false,
  onFilesChanged = null,

  // `OrderInlineEditor` pass-through bag — reference data + the save-then-act
  // wiring. Forwarded UNTOUCHED; see the docblock above.
  editor = {},
}) {
  // ── THE SAVE-EPOCH GUARD (2026-09-13, review F1) ────────────────────────
  // 🚨 **DECLARED BEFORE THE `!detail` EARLY RETURN, AND IT MUST BE.** A hook
  // after a conditional return is a hooks-order violation the moment `detail`
  // arrives, which is every expand.
  //
  // This component is the ONLY one that has both file surfaces of an expanded
  // row as children — `OrderInlineEditor` (which owns the PUT) and
  // `OrderExpandedDetails` (whose drop zone re-fetches the same cache entry) —
  // so it is the only place a counter they BOTH read can live. The editor bumps
  // it on a successful save; each surface captures it before its own detail
  // re-fetch and re-reads it after, and drops a response the save has
  // superseded. The whole sequence, and why a `useLatestRequest` sequence is
  // not the fix, is at `utils/orderFileMutations.js`'s `refreshOrderFiles`.
  //
  // 🔑 A REF, never state: nothing renders from it, and a re-render per upload
  // would be a second defect wearing a fix's clothes.
  const saveEpochRef = useRef(0);

  if (!detail) return null;

  const tid = (suffix) => `${testIdPrefix}-${suffix}-${order.id}`;

  // Rejection reason — trivial derivation from `detail`, computed here rather
  // than passed in so both branches' grid read the SAME node without the
  // page building it twice.
  // 🔀 2026-10-19 (owner): ONE line, "Rejected by <name>, <date> — <reason>";
  // who/when from the LIST row's `rejection_info` — see `OrderRejectionLine`.
  const rejectionReasonNode = detail.rejection_reason ? (
    <OrderRejectionLine
      className="sm:col-span-2"
      reason={detail.rejection_reason}
      info={order.rejection_info || null}
      testId={tid('rejection')}
    />
  ) : null;

  // PO editor for a MANAGER viewing the READ-ONLY branch — the dedicated
  // `PATCH /:id/po-number` endpoint, live regardless of the edit window.
  // `OrderForm` below is told `hidePoNumberField={canManage}` so its OWN PO
  // field is suppressed for exactly this viewer — see that prop's doc in
  // `OrderForm.js` — so this is the ONLY PO control a manager sees on a
  // non-editable order. A viewer WITHOUT manage rights sees PO on the form
  // itself instead, as plain text (2026-08-16 — previously PO was invisible
  // to a plain requester in read-only mode at all, which contradicted the
  // owner's ruling that a requester must see every field on their own
  // order; `OrderForm`'s `showPoNumberField` closes that gap).
  const poEditorNode = canManage ? (
    <p>
      <InlinePoNumberEditor
        order={detail}
        onSaved={onPoNumberSaved}
        testId={tid('po-number')}
      />
    </p>
  ) : null;

  // Expected delivery date, beside the PO editor — owner ruling 2026-08-19.
  // 🔑 THREE-WAY BRANCH, mirroring the receive-ticks greyed-until-ordered
  // precedent:
  //   - before `ordered` — NOTHING (no control at all; nothing has been
  //     placed, so there is no supplier ETA to record or show).
  //   - `ordered` / `partially_delivered`, MANAGER — the live editor
  //     (`PATCH /:id/expected-delivery`, gated `canManageDepartmentOrders`
  //     in the order's own department).
  //   - everything else that has a date to show (a non-manager viewing
  //     `ordered`/`partially_delivered`, or ANYONE viewing `delivered` —
  //     "historical") — the shared read-only badge, same component
  //     `MyOrders.js` uses, extracted so the two can never disagree.
  //
  // 🚨 **"HAS A DATE TO SHOW" MUST BE TESTED HERE, NOT LEFT TO THE BADGE —
  // fixed 2026-09-12, wrong since the pairing landed 2026-08-19.**
  // `ExpectedDeliveryBadge` returns `null` without a stored
  // `expected_delivery_date` (its own guard, `ExpectedDeliveryBadge.js`), but
  // the ELEMENT `<ExpectedDeliveryBadge …/>` is TRUTHY — so
  // the old paired sub-grid read "Expected Delivery is present" while
  // receiving no child for it, and Admin Notes auto-placed into the 14rem
  // track. (🔻 Both fields left that sub-grid on 2026-09-13 and the sub-grid
  // is deleted — so that SYMPTOM is gone, but this guard is NOT residue: it
  // is now what stops a phantom node adding a whole TRACK to `OrderForm`'s
  // per-role Order Details grid, which is the dead-trailing-whitespace defect
  // that grid's track arithmetic exists to prevent.) Owner,
  // 2026-09-12, Department Orders, a `delivered`
  // order with no ETA: *"Admin Notes on an expanded order in Dept orders is
  // not full width? The text input box is very narrow?"* The badge's own
  // guard STAYS (shared with `MyOrders.js`, defensive); this is the CALLER
  // answering "will it render?" for itself. Only the DATE half needs
  // mirroring — every branch that reaches `expectedDeliveryBadgeNode` is
  // already one of the badge's own visible statuses. The MANAGER editor
  // branch is deliberately untouched: `InlineExpectedDeliveryEditor` renders
  // its input with or without a stored date (that is the control for setting
  // one), so it is never a phantom child.
  const expectedDeliveryBadgeNode = detail.expected_delivery_date ? (
    <ExpectedDeliveryBadge order={detail} testId={tid('expected-delivery')} className="" />
  ) : null;
  const expectedDeliveryNode = EXPECTED_DELIVERY_EDITABLE_STATUSES.includes(detail.status) ? (
    canManage ? (
      <p>
        <InlineExpectedDeliveryEditor
          order={detail}
          onSaved={onExpectedDeliverySaved}
          testId={tid('expected-delivery')}
        />
        {/* Helper note under the MANAGER's editable ETA control only — owner
            request 2026-08-19 (wording proposed by the implementer, not the
            owner's own text, which had a typo: "forcast"). Not shown on the
            read-only badge branches below: there is no action to prompt a
            viewer who cannot edit the field. 🔻 It USED to be what made
            Expected Delivery the taller of the two paired cells in the
            14rem sub-grid, which Admin Notes' default height had to match;
            both fields left that pairing on 2026-09-13 and the equal-height
            machinery went with it. The note itself stays — it is the
            owner's copy, not layout scaffolding. */}
        <span
          className="block mt-1 text-xs text-gray-500 dark:text-gray-400"
          data-testid={tid('expected-delivery-helper')}
        >
          Update delivery details with accurate forecast from supplier
        </span>
      </p>
    ) : (
      expectedDeliveryBadgeNode
    )
  ) : detail.status === 'delivered' ? (
    expectedDeliveryBadgeNode
  ) : null;

  // Expected Delivery, ALONE in its narrow track.
  //
  // 🔻 **IT WAS PAIRED WITH ADMIN NOTES FROM 2026-08-19 UNTIL 2026-09-13, AND
  // THAT PAIRING IS NOW DELETED BY OWNER RULING — A DATED CONTRACT CHANGE, NOT
  // A TIDY-UP.** Owner, 2026-09-13, on the renamed Internal notes field: *"lets
  // move it to the order details section"*, then, same day: *"Internal notes
  // goes next to Expected delivery date"*. The field now renders as the
  // SECOND cell of `OrderForm`'s Order Details ROW 2, beside Expected
  // Delivery, through that component's `orderDetailsRow2Rest` slot, in BOTH
  // branches below — so it is no longer a child of this grid at all and this
  // node no longer takes it.
  //
  // What the pairing was, so nobody rebuilds it by accident: a dedicated
  // narrow-then-flexible sub-grid (`14rem` then `1fr`) holding Expected
  // Delivery in track 1 and Admin Notes in track 2, answering the owner's
  // 2026-08-19 request *"put the new Expected Delivery: next to and before
  // admin notes in a small width cell … admin notes doesnt need to be full
  // width"*. Three pieces of machinery existed ONLY to serve it and go with it:
  // the `expectedDeliveryNode ? undefined : 'sm:col-span-2'` test on the notes
  // wrapper (the 2026-09-12 phantom-child fix — see `expectedDeliveryNode`
  // above, whose own "will it render?" guard STAYS and is now load-bearing for
  // this node alone), the `items-stretch` equal-height requirement, and
  // `DepartmentOrders.js`'s `h-full flex flex-col` + `flex-1 h-full` textarea
  // plumbing that made the notes box match the ETA cell's height. **Do not
  // re-add a second child to this sub-grid without re-reading all three.**
  //
  // 🔻 **AND THE NARROW 14rem TRACK IS GONE TOO, SAME DAY, SAME RULING.**
  // Owner, 2026-09-13: *"Expected delivery can also go in the Order Details
  // section? this then clears the space occupied by the admin notes and
  // expected delivery date."* — which SUPERSEDES the 2026-08-19 request that
  // built the sub-grid (*"put the new Expected Delivery: next to and before
  // admin notes in a small width cell … admin notes doesnt need to be full
  // width"*). There is no pair left to be small BESIDE in THIS component: the
  // field is now the FIRST cell of `OrderForm`'s own Order Details ROW 2,
  // pinned under PO Number at the same HALF `xl:col-span-1` budget, which is
  // the "small width cell" the 2026-08-19 request was reaching for anyway.
  // It goes down as `orderDetailsRow2Lead`, a finished node — `OrderForm`
  // never learns what is in it.
  //
  // 🚨 `expectedDeliveryNode` is `null`, not a phantom element, when there is
  // nothing to show — the 2026-09-12 fix above. That still matters: a truthy
  // node that renders nothing still occupies row 2's lead CELL (col 1 stays
  // reserved and empty rather than letting Internal notes spread into it) —
  // see `orderDetailsRow2RestClass` in `OrderForm.js`, which reads the PROP,
  // never the rendered element, for exactly this reason.

  // Received-tick plumbing for `OrderForm`'s optional `received` prop.
  // 🔑 BUILT ONCE, read by BOTH branches (until 2026-08-16 the edit branch
  // built its own separate copy further down — same shape, same inputs,
  // just duplicated). Never a second source of truth for "is this item
  // received".
  const receivedProp = showReceivedTicks ? {
    // 🪤 THE MAP IS DELIBERATELY UNFILTERED AND THE COUNTS ARE NOT. This is
    // keyed BY ITEM ID and `OrderForm` reads it per rendered row — an
    // adjustment line still RENDERS on the order (it is a real line carrying
    // real money), so dropping it here would make its checkbox read `undefined`
    // and paint as unticked-and-tickable. The tick is disabled for it by the
    // server's refusal (`item_not_receivable`), not by hiding it from the map.
    items: lineStepMap(detail.items),
    orderedCount,
    canOrder: !!canOrderItems,
    orderLockedReason: RECEIVING_LOCKED_TOOLTIP,
    onToggleOrdered: (itemId, current) => onToggleItemOrdered(order.id, itemId, current),
    onOrderAll: (next) => onOrderAll(order.id, next),
    // ⚖️ ...whereas the PAIR below is the delivery statement "N of M received",
    // and that is goods only. Both come from the page's one `countDelivery`.
    receivedCount,
    total: receivableCount,
    busy: receivingThisOrder,
    locked: !canReceiveNow,
    lockedReason: RECEIVING_LOCKED_TOOLTIP,
    onToggleItem: (itemId, current) => onToggleItemReceived(order.id, itemId, current),
    onReceiveAll: (next) => onReceiveAll(order.id, next),
  } : null;

  // ── PER-LINE-ITEM ATTACHMENTS IN THE EDIT BRANCH (2026-09-13) ───────────
  // Owner, asked whether it had to exist before `/orders/:id` retires: *"Yes
  // attachments per item is needed"*. Forwarded to `OrderInlineEditor` beside
  // `canRemoveFile` / `onFilesChanged`, which this component already receives —
  // it still resolves nothing.
  //
  // 🚨 **`canUploadDelivery` IS THE MIRROR, AND IT IS ONE TERM WIDE ON ONE PAGE
  // — STATED RATHER THAN LEFT TO BE DISCOVERED.** The server gate is
  // `requireItemFilesAuthorityBeforeUpload` (`server/routes/orders.js`, grep
  // `router.post('/:id/items/:itemId/files'`): `isGlobalAdmin ||
  // canManageDepartmentOrders(the ORDER'S OWN dept) || isOwner`. Against what
  // each page passes as `canUploadDelivery`:
  //   • `Approvals.js` — `isAdmin || canManageOrder(detail) || isOrderOwner`.
  //     **Byte-for-byte the same three terms.**
  //   • `MyOrders.js` — hard `true`, and every row there is the viewer's own
  //     order (`GET /api/orders?user_id=<self>`), so `isOwner` holds.
  //   • `DepartmentOrders.js` — those three PLUS `canManageStores`, which the
  //     item-files route does NOT admit. A Stores user who is neither admin,
  //     nor a manager of this order's department, nor its owner is therefore
  //     OFFERED the control and REFUSED by the server, which answers *"You can
  //     only upload files to your own orders"* — surfaced verbatim by the
  //     editor's toast.
  // 🔑 That is the sanctioned direction and it is deliberate: the client cannot
  // resolve `isOwner` HERE (this component holds no viewer identity, by design,
  // and the pages own that answer), so it OVER-offers to one narrow class and
  // lets the server refuse, rather than UNDER-offering and hiding a control the
  // server allows — which would deny every plain requester editing their own
  // rejected order, the commonest case of all. Narrowing it exactly means a
  // page-resolved `canAttachItemFiles` prop; all three pages already compute
  // `isOrderOwner`, so it is one line each when someone owns those files.
  const canAttachItemFiles = canUploadDelivery;

  const expandedDetailsNode = (
    <OrderExpandedDetails
      order={detail}
      canUploadDelivery={canUploadDelivery}
      canRemoveFile={canRemoveFile}
      onFilesChanged={onFilesChanged}
      saveEpochRef={saveEpochRef}
      testIdPrefix={`${testIdPrefix}-${order.id}`}
    />
  );

  // ═══════════════════════════════════════════════════════════════════════
  // 🔀 THE READ-ONLY BRANCH — `OrderForm` IN `readOnly` MODE, THE SAME FORM
  //    THE EDIT BRANCH RENDERS BELOW (2026-08-16) — SUPERSEDES THE BESPOKE
  //    `readOnlyPanelBody` THIS REPLACED (a hand-rolled metadata grid + a
  //    separate mobile-card/desktop-table line-items renderer).
  // ═══════════════════════════════════════════════════════════════════════
  //
  // 🎬 THE PRODUCT PROBLEM THIS FIXES: the owner flagged THREE TIMES that My
  // Orders' expanded view doesn't look/feel like Department Orders'. Root
  // cause: Dept Orders' edit branch showed the full `OrderForm`; the
  // read-only branch (both pages, whenever `showInlineEditor` is false —
  // which is every CARD-layout row regardless of status, and every
  // non-editable order in table layout) showed a SEPARATE, SPARSER panel
  // that could only ever drift from the form's own field set. His decision,
  // over a mirror: *"Reuse the actual edit form, view-only"* — render the
  // SAME `OrderForm` in `readOnly` mode so the two are identical BY
  // CONSTRUCTION and cannot miss a field again.
  //
  // 🚨 `OrderForm.js` ALREADY HAD A COMPLETE `readOnly` MODE, BUILT EARLIER
  // FOR `OrderPage.js`'s `/orders/:id` VIEW MODE. Every field this panel
  // used to hand-roll — PO, Requested By, Sub Department, Job/Rig/Gag,
  // Supplier + Exclusive, Needed By, Lead Time, Notes/Comments, and the
  // per-item fields — already had a `readOnly ? <ReadOnlyValue> : <input>`
  // branch, and the form already accepted the `received` prop above. So
  // this is REUSE, not new UI — the work is entirely in not losing what the
  // old bespoke panel did that `OrderForm` didn't.
  //
  // 🔑 THE COEXISTENCE PROBLEM, AND WHY IT IS THE HARD PART. Four controls
  // in the old panel wrote through DEDICATED endpoints, independent of the
  // form's own PUT, and therefore worked on a non-editable (e.g. `approved`)
  // order — which `OrderForm`'s stock `readOnly` mode, built for a page that
  // never had them, does not reproduce:
  //   1. PO Number (`InlinePoNumberEditor`, `PATCH /:id/po-number`) — solved
  //      by `hidePoNumberField={canManage}`, which suppresses the form's OWN
  //      PO field for a manager so `poEditorNode` is the only PO control they
  //      see; a non-manager sees the form's (now visible, see its own note)
  //      read-only PO text and no layered editor, so still exactly one.
  //      🔀 **2026-09-13: the editor no longer sits in a band ABOVE the form —
  //      it is handed IN, through `OrderForm`'s `poNumberFieldSlot`, and
  //      renders inside the PO CELL of the Order Details grid.** Exactly one
  //      control either way; what changed is where it is drawn, and that the
  //      manager's PO now lines up with the column the read-only PO occupies
  //      for everyone else.
  //   2. Item Cost Code / Payment Type (`InlineLineItemSelect`, `PATCH
  //      /:id/items/:itemId/cost-code` / `…/payment-type`) — `OrderForm`
  //      gained two ADDITIVE optional props for exactly this,
  //      `onReadOnlyCostCodeChange` / `onReadOnlyPaymentTypeChange`: passed
  //      only for `canManage`, they make the form render a LIVE `<select>`
  //      instead of static text, wired to these dedicated handlers instead
  //      of local form state. Every other caller of `OrderForm` (incl.
  //      `OrderPage.js`'s own view mode) passes neither and is unaffected.
  //   3. The per-item Notify/flag menu (`OrderItemActionsMenu`, `POST|DELETE
  //      /:id/items/:itemId/notify`) — same shape, `onReadOnlyItemNotifyUpdated`.
  //      `OrderForm` used to suppress this menu outright whenever `readOnly`
  //      was true (built for `OrderPage.js`'s view mode, which never had it
  //      either); the new prop re-admits it, gated on `canManage`, with no
  //      `extraItems` (no add/remove-item editing exists in view mode).
  //   4. Rejection reason / the status override (`InlineStatusJump`) / the
  //      Actions menu — NO conflict, and no change needed. `OrderForm` has NO
  //      `admin_notes` field and NO status control at all, so
  //      `rejectionReasonNode` simply keeps rendering in the small grid above
  //      the form, exactly as the edit branch already does below.
  //      🔻 `adminNotesNode` was in this list until 2026-09-13; it now goes
  //      INTO the form, through `OrderForm`'s `orderDetailsRow2Rest` slot, on
  //      both branches. Still a page-built, already-gated node — the form has
  //      no field for it and resolves nothing about it. `InlineStatusJump` lives in the
  //      Actions-menu footer (part of `quickActionsRow`), entirely outside
  //      both this grid and the form — untouched.
  //
  // 🔑 `headerActions={quickActionsRow}` — the SAME slot the edit branch
  // already feeds via `OrderInlineEditor` (as `quickActionsHeaderGroup`,
  // built identically on both pages). One header component
  // (`components/OrderSectionHeader.js`, inside `OrderForm` itself), one
  // slot, whichever branch renders — no second `OrderSectionHeader` call
  // here any more.
  //
  // 🔑 `received={receivedProp}` keeps the owner's received-ticks +
  // Select-all LIVE for the order's owner once it is ordered — a
  // non-negotiable from the brief. `expandedDetailsNode` (Attachments /
  // Delivery Paperwork / Activity history / Approval stepper) renders AFTER
  // the form exactly as it always has, so delivery-paperwork upload is
  // untouched by any of this — it was never inside the old panel or the
  // form to begin with.
  //
  // 🟡 A REAL, INTENDED SIDE EFFECT: this changes Dept Orders' OWN read-only
  // branch layout too (its line items go from a compact table to `OrderForm`'s
  // collapsible item CARDS, `initialItemsCollapsed`) — the literal, chosen
  // consequence of "identical by construction", not a regression. Flagged
  // for a staging look, not hidden.
  const readOnlyExpandedContent = (
    <div>
      {rejectionReasonNode && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm mb-3">
          {rejectionReasonNode}
        </div>
      )}
      <OrderForm
        mode="edit"
        readOnly
        initialOrder={detail}
        productions={editor.productions}
        suppliers={editor.suppliers}
        supplierSuggestions={editor.supplierSuggestions}
        // Which reference lists failed to load — see `OrderForm`'s
        // `loadFailures` prop. Carried on the `editor` bag alongside the arrays
        // it describes, so a page cannot supply one without the other.
        loadFailures={editor.loadFailures}
        headerActions={quickActionsRow}
        headerActionsBelow={headerActionsBelow}
        orderDetailsRow2Rest={adminNotesNode}
        poNumberFieldSlot={poEditorNode}
        orderDetailsRow2Lead={expectedDeliveryNode}
        received={receivedProp}
        gags={editor.gags}
        subDepartments={editor.subDepartments}
        costCodes={costCodes}
        paymentTypes={paymentTypes}
        userAssignedProductions={editor.userAssignedProductions}
        hidePoNumberField={canManage}
        onReadOnlyCostCodeChange={canManage ? (itemId, value) => onCostCodeChange(order.id, itemId, value) : null}
        onReadOnlyPaymentTypeChange={canManage ? (itemId, value) => onPaymentTypeChange(order.id, itemId, value) : null}
        onReadOnlyItemNotifyUpdated={canManage ? (updated) => onItemNotifyUpdated(order.id, updated) : null}
        itemExtraActions={itemExtraActions}
        itemHeaderTick={itemHeaderTick}
        initialItemsCollapsed
      />
      {expandedDetailsNode}
    </div>
  );

  if (!showInlineEditor) return readOnlyExpandedContent;

  // ── EXPAND-TO-EDIT (edit branch) — UNCHANGED except that `receivedProp`
  // is now the ONE definition built above, shared with the read-only branch,
  // rather than a second copy. ──────────────────────────────────────────
  return (
    <div>
      {/* 🔑 THE CONTROLS `OrderForm` HAS NO SLOT OR FIELD FOR — the rejection
          reason and (unlike PO) the expected delivery date, which has no field
          in `OrderForm` at all in either mode. Internal notes LEFT this grid on
          2026-09-13 for the form's own `orderDetailsRow2Rest` slot. Everything else the
          read-only panel holds is superseded by the form's own fields;
          rendering a second copy would be a worse defect than the one this
          branch fixes (two PO inputs, or two line-item lists, on one panel
          that can disagree). */}
      {rejectionReasonNode && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm mb-3">
          {rejectionReasonNode}
        </div>
      )}
      <OrderInlineEditor
        order={detail}
        productions={editor.productions}
        suppliers={editor.suppliers}
        supplierSuggestions={editor.supplierSuggestions}
        loadFailures={editor.loadFailures}
        headerActions={quickActionsHeaderGroup}
        orderDetailsRow2Rest={adminNotesNode}
        orderDetailsRow2Lead={expectedDeliveryNode}
        received={receivedProp}
        gags={editor.gags}
        subDepartments={editor.subDepartments}
        costCodes={costCodes}
        paymentTypes={paymentTypes}
        userAssignedProductions={editor.userAssignedProductions}
        collapseRequestSignal={editor.collapseRequestSignal}
        onCollapsePromptResolved={editor.onCollapsePromptResolved}
        onDirtyChange={editor.onDirtyChange}
        onSaved={editor.onSaved}
        onCancel={editor.onCancel}
        saveRequestRef={editor.saveRequestRef}
        canAttachItemFiles={canAttachItemFiles}
        canRemoveFile={canRemoveFile}
        onFilesChanged={onFilesChanged}
        saveEpochRef={saveEpochRef}
        itemExtraActions={itemExtraActions}
        itemHeaderTick={itemHeaderTick}
        // The page's existing cancel flow (null = may not cancel here) —
        // `OrderForm`'s only-line Remove hands over to it (2026-10-01).
        onRequestCancelOrder={editor.onRequestCancelOrder || null}
        testId={tid('inline-editor')}
      />
      {/* 🔑 THE SAME NODE THE READ-ONLY BRANCH RENDERS, AND IT MUST STAY IN
          BOTH — attachments, the approval stepper and activity history have
          no counterpart inside `OrderForm`, so there is no way for two
          copies to disagree. */}
      {expandedDetailsNode}
    </div>
  );
}

export default OrderExpandedRow;

